import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/Button';
import { PageHeader } from '../../shared/ui/PageHeader';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import {
  getCurrentInstitute,
  getInstituteSettings,
  INSTITUTE_QUERY_KEYS,
  setInstituteFeatures,
  type InstituteSettings,
} from './api';
import { EventTypesPanel } from './EventTypesPanel';
import { InstituteLogoControl } from './InstituteLogoControl';
import s from './SettingsPage.module.css';

export function InstituteSettingsPage(): JSX.Element {
  const queryClient = useQueryClient();

  const currentInstituteQuery = useQuery({
    queryKey: INSTITUTE_QUERY_KEYS.current,
    queryFn: getCurrentInstitute,
  });

  const instituteId = currentInstituteQuery.data?.institute_id ?? null;

  const settingsQuery = useQuery({
    queryKey:
      instituteId !== null
        ? INSTITUTE_QUERY_KEYS.settings(instituteId)
        : ['institute', 'settings', 'none'],
    queryFn: () => getInstituteSettings(instituteId as string),
    enabled: instituteId !== null,
  });

  if (currentInstituteQuery.isPending) {
    return <p className={s.loading}>Loading institute…</p>;
  }
  if (currentInstituteQuery.error !== null) {
    return (
      <StatusBanner tone="error">
        Could not load your institute: {currentInstituteQuery.error.message}
      </StatusBanner>
    );
  }
  if (currentInstituteQuery.data === null) {
    return (
      <StatusBanner tone="warning">
        You are not an active member of any institute. Ask your superadmin to assign you.
      </StatusBanner>
    );
  }

  if (settingsQuery.isPending || settingsQuery.data === undefined) {
    return <p className={s.loading}>Loading settings…</p>;
  }
  if (settingsQuery.error !== null) {
    return (
      <StatusBanner tone="error">
        Could not load settings: {settingsQuery.error.message}
      </StatusBanner>
    );
  }

  return (
    <SettingsForm
      settings={settingsQuery.data}
      onSaved={(updated) => {
        queryClient.setQueryData<InstituteSettings>(
          INSTITUTE_QUERY_KEYS.settings(updated.institute_id),
          updated,
        );
      }}
    />
  );
}

interface SettingsFormProps {
  settings: InstituteSettings;
  onSaved: (next: InstituteSettings) => void;
}

function SettingsForm({ settings, onSaved }: SettingsFormProps): JSX.Element {
  const canEdit = settings.caller_can_edit;
  const [logoPath, setLogoPath] = useState(settings.logo_path);
  const [aiTutorEnabled, setAiTutorEnabled] = useState(settings.ai_tutor_enabled);
  const [pdfIngestEnabled, setPdfIngestEnabled] = useState(settings.pdf_ingest_enabled);
  const [aiReviewEnabled, setAiReviewEnabled] = useState(settings.ai_review_enabled);
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const togglesChanged = useMemo(
    () =>
      aiTutorEnabled !== settings.ai_tutor_enabled ||
      pdfIngestEnabled !== settings.pdf_ingest_enabled ||
      aiReviewEnabled !== settings.ai_review_enabled,
    [
      aiTutorEnabled,
      pdfIngestEnabled,
      aiReviewEnabled,
      settings.ai_tutor_enabled,
      settings.pdf_ingest_enabled,
      settings.ai_review_enabled,
    ],
  );

  const mutation = useMutation({
    mutationFn: setInstituteFeatures,
    onSuccess: (data) => {
      const next: InstituteSettings = {
        ...settings,
        ai_tutor_enabled: data.ai_tutor_enabled,
        pdf_ingest_enabled: data.pdf_ingest_enabled,
        ai_review_enabled: data.ai_review_enabled,
      };
      onSaved(next);
      setAiTutorEnabled(next.ai_tutor_enabled);
      setPdfIngestEnabled(next.pdf_ingest_enabled);
      setAiReviewEnabled(next.ai_review_enabled);
      setSavedMsg('Feature toggles saved.');
    },
    onError: (err: Error) => setServerError(err.message),
  });

  function handleSaveToggles(): void {
    if (!canEdit || !togglesChanged || mutation.isPending) return;
    setServerError(null);
    setSavedMsg(null);
    mutation.mutate({
      institute_id: settings.institute_id,
      ai_tutor_enabled: aiTutorEnabled,
      pdf_ingest_enabled: pdfIngestEnabled,
      ai_review_enabled: aiReviewEnabled,
    });
  }

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Institute"
        title="Settings"
        subtitle="Turn individual features on or off for your students and staff."
        meta={
          <div className={s.identity}>
            <strong>{settings.name}</strong>
            <span aria-hidden="true">·</span>
            <code>{settings.slug}</code>
          </div>
        }
        actions={
          canEdit ? (
            <Button variant="secondary" asChild>
              <Link to="/institute/settings/email">Email</Link>
            </Button>
          ) : undefined
        }
      />

      {!canEdit ? (
        <StatusBanner tone="info">
          Read-only — only owners and admins can change institute settings.
        </StatusBanner>
      ) : null}

      <section className={s.card} aria-labelledby="branding-card-title">
        <div className={s.cardHead}>
          <h2 id="branding-card-title" className={s.cardTitle}>
            Branding
          </h2>
          <p className={s.cardSubtitle}>
            Your logo appears on every printed bill. Position and accent color are set on the
            Finance settings page, under Template.
          </p>
        </div>
        <InstituteLogoControl
          instituteId={settings.institute_id}
          logoPath={logoPath}
          canEdit={canEdit}
          onSaved={(next) => {
            setLogoPath(next);
            onSaved({ ...settings, logo_path: next });
          }}
        />
      </section>

      <section className={s.card} aria-labelledby="features-card-title">
        <div className={s.cardHead}>
          <h2 id="features-card-title" className={s.cardTitle}>
            Feature toggles
          </h2>
          <p className={s.cardSubtitle}>
            Listening and Reading are always scored automatically. These toggles control how Writing
            and Speaking are graded, plus other optional features.
          </p>
        </div>

        <div className={s.toggleRow}>
          <div className={s.toggleText}>
            <span className={s.toggleName}>AI Tutor</span>
            <span className={s.toggleDesc}>
              Students see a chat tutor that answers questions about IELTS criteria and their recent
              attempts.
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={aiTutorEnabled}
            aria-label="AI Tutor enabled"
            disabled={!canEdit || mutation.isPending}
            className={cn(s.switch, aiTutorEnabled && s.switchOn)}
            onClick={() => setAiTutorEnabled((v) => !v)}
          />
        </div>

        <div className={s.toggleRow}>
          <div className={s.toggleText}>
            <span className={s.toggleName}>AI review of Writing &amp; Speaking</span>
            <span className={s.toggleDesc}>
              The default for new mock sittings: AI grades Writing and Speaking and pre-fills the
              rubric for your examiner. Turn it off to grade those two by hand — bands stay blank
              until an examiner enters them. Any sitting can override this when you schedule it.
              Practice attempts always use AI, because they never enter the grading queue.
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={aiReviewEnabled}
            aria-label="AI review of Writing and Speaking enabled"
            disabled={!canEdit || mutation.isPending}
            className={cn(s.switch, aiReviewEnabled && s.switchOn)}
            onClick={() => setAiReviewEnabled((v) => !v)}
          />
        </div>

        <div className={s.toggleRow}>
          <div className={s.toggleText}>
            <span className={s.toggleName}>PDF Ingest</span>
            <span className={s.toggleDesc}>
              Allow institute staff to upload Cambridge-style PDFs for automatic question
              extraction. Disable to block uploads entirely.
            </span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={pdfIngestEnabled}
            aria-label="PDF Ingest enabled"
            disabled={!canEdit || mutation.isPending}
            className={cn(s.switch, pdfIngestEnabled && s.switchOn)}
            onClick={() => setPdfIngestEnabled((v) => !v)}
          />
        </div>

        <div className={s.actions}>
          {savedMsg !== null ? (
            <span className={cn(s.actionsHint, s.savedHint)} role="status">
              {savedMsg}
            </span>
          ) : null}
          <Button
            type="button"
            className={s.submit}
            onClick={handleSaveToggles}
            disabled={!canEdit || !togglesChanged}
            loading={mutation.isPending}
          >
            Save feature toggles
          </Button>
        </div>

        {serverError !== null ? <StatusBanner tone="error">{serverError}</StatusBanner> : null}
      </section>

      <section className={s.card} aria-labelledby="event-types-card-title">
        <div className={s.cardHead}>
          <h2 id="event-types-card-title" className={s.cardTitle}>
            Activity types
          </h2>
          <p className={s.cardSubtitle}>
            The things staff can log against a student — payments, calls, class bookings, test
            dates, visa milestones.
          </p>
        </div>
        <EventTypesPanel instituteId={settings.institute_id} canEdit={canEdit} />
      </section>
    </div>
  );
}
